/**
 * Base API Service Layer
 * 
 * This module provides the foundation for all API calls.
 * API Base URL is /api which is proxied to the linked function app backend.
 */

import { logApiCall } from '../components/dev';

// ============================================================================
// CONFIGURATION
// ============================================================================

export const API_BASE_URL = '/api';

// ============================================================================
// USER CONTEXT FOR API CALLS
// ============================================================================

/**
 * Module-level store for current user ID.
 * Sent as x-user-id header — a non-trusted display hint only.
 * The backend derives authoritative identity from SWA client principal headers.
 */
let currentUserId: string | null = null;

/**
 * Set the current user ID for API calls.
 * Called by UserContext when user logs in.
 */
export function setCurrentUserId(userId: string | null): void {
  currentUserId = userId;
}

/**
 * Get the current user ID.
 * Used internally for API requests.
 */
export function getCurrentUserId(): string | null {
  return currentUserId;
}

// ============================================================================
// TYPES
// ============================================================================

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
  /**
   * Machine-readable error class from the backend body (`category`), e.g.
   * `license_missing` from the hunting endpoints. Lets the UI render
   * "not licensed" differently from "the query failed".
   */
  errorCategory?: string;
  /**
   * The parsed JSON body of a failed response, when there was one. Lets a
   * caller read structured guidance the backend sent with a refusal (e.g. the
   * closure guard's `explanation`) instead of only a one-line message.
   */
  errorBody?: unknown;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ApiError {
  message: string;
  code?: string;
  status: number;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Simulates network delay for mock data (only used in development fallback)
 */
export function delay(ms: number = 300): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Creates a successful API response wrapper
 */
export function success<T>(data: T): ApiResponse<T> {
  return { data, success: true };
}

/**
 * Creates an error API response wrapper
 */
export function error<T>(message: string): ApiResponse<T> {
  return { data: null as unknown as T, success: false, error: message };
}

// ============================================================================
// FETCH WRAPPER
// ============================================================================

/**
 * Wrapper around fetch for API calls
 * Handles error handling and response parsing
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  const method = (options.method || 'GET').toUpperCase() as 'GET' | 'POST' | 'PATCH' | 'DELETE';
  const payload = options.body ? JSON.parse(options.body as string) : undefined;
  
  // Log the API call
  const logger = logApiCall(method, endpoint, payload);
  const startTime = performance.now();
  
  try {
    // Build headers with optional user ID
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Include user ID if available (for auth)
    if (currentUserId) {
      headers['x-user-id'] = currentUserId;
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    const duration = Math.round(performance.now() - startTime);

    if (!response.ok) {
      if (response.status === 401) {
        logger.error('Session expired (401)', duration);
        // Logout first to clear the stale SWA session cookie, then redirect back to login
        window.location.href =
          '/.auth/logout?post_logout_redirect_uri=' +
          encodeURIComponent('/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname));
        return { data: null as unknown as T, success: false, error: 'Session expired' };
      }

      const errorBody = await response.text();
      let errorMessage = `API error: ${response.status}`;
      let errorCategory: string | undefined;
      let parsedErrorBody: unknown;
      
      try {
        const errorJson = JSON.parse(errorBody);
        parsedErrorBody = errorJson;
        errorMessage = errorJson.error || errorJson.message || errorMessage;
        if (typeof errorJson.category === 'string') {
          errorCategory = errorJson.category;
        }
      } catch {
        // Body wasn't JSON
      }
      
      logger.error(errorMessage, duration);
      
      return {
        data: null as unknown as T,
        success: false,
        error: errorMessage,
        errorCategory,
        errorBody: parsedErrorBody,
      };
    }

    if (response.status === 204) {
      logger.success(null, duration);
      return { data: undefined as T, success: true };
    }

    const responseText = await response.text();
    const data = responseText ? JSON.parse(responseText) as T : (undefined as T);
    logger.success(data, duration);
    return { data, success: true };
  } catch (err) {
    const duration = Math.round(performance.now() - startTime);
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error(`Network error: ${message}`, duration);
    return {
      data: null as unknown as T,
      success: false,
      error: `Network error: ${message}`,
    };
  }
}

/**
 * POST request helper
 */
export async function apiPost<T>(
  endpoint: string,
  body: unknown
): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * PATCH request helper
 */
export async function apiPatch<T>(
  endpoint: string,
  body: unknown
): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * DELETE request helper
 */
export async function apiDelete<T>(
  endpoint: string
): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, {
    method: 'DELETE',
  });
}

// ============================================================================
// CONNECTION STATUS
// ============================================================================

/**
 * Returns whether the API is connected
 */
export function isApiConnected(): boolean {
  return true; // Now connected to real API via SWA linked backend
}

/**
 * Health check for the API
 * Returns true if the API is reachable
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Builds a query string from filter parameters
 */
export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          searchParams.set(key, value.join(','));
        }
      } else {
        searchParams.set(key, String(value));
      }
    }
  }
  
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}
